<template>
  <div class="counter" :class="type">
    <div>
      <h2>{{ title }}</h2>
      <h3 v-if="subtitle">{{ subtitle }}</h3>
    </div>
    <div class="counter__items">
      <div class="counter__item" :data-qa="`frv-${frvType}-counter-actual`">
        <h4>Фактический</h4>
        <p>
          <span :class="[colorIndicator(info.actual), { indicator: showIndicator }]"></span
          ><b :data-qa="`frv-${frvType}-counter-actual-value`">{{ info.actual }} ч</b>
          <span class="counter__proc" v-if="showIndicator" :data-qa="`frv-${frvType}-counter-actual-pct`">{{ calculate(info.actual) }}%</span>
        </p>
      </div>
      <div class="counter__item" :data-qa="`frv-${frvType}-counter-planned`">
        <h4>Запланированный</h4>
        <p>
          <span
            :class="[colorIndicator(info.planned + info.new), { indicator: showIndicator }]"
          ></span
          ><b :data-qa="`frv-${frvType}-counter-planned-value`">{{ formatNumber(info.planned, info.new) }} ч</b>
          <span class="counter__proc" v-if="showIndicator" :data-qa="`frv-${frvType}-counter-planned-pct`"
            >{{ calculate(info.planned + info.new) }}%</span
          >
        </p>
      </div>
      <div
        class="counter__item"
        :data-qa="`frv-${frvType}-counter-dedicated`"
        v-if="info.dedicated || info.dedicated?.toString() === '0'"
      >
        <h4>Выделенный</h4>
        <p>
          <b :data-qa="`frv-${frvType}-counter-dedicated-value`">{{ info.dedicated }} ч</b>
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { CounterPanel } from '@/types'

interface Props {
  title: string
  subtitle?: string
  info: CounterPanel
  type?: string
  showIndicator?: boolean
  frvType: 'main' | 'rv'
}

const props = withDefaults(defineProps<Props>(), {
  showIndicator: true,
})

const calculate = (col: number): number => {
  if (props.info.dedicated) {
    const oneProc = props.info.dedicated / 100
    return +(col / oneProc).toFixed(1)
  } else {
    return 0
  }
}

const formatNumber = (planned: number, actual: number) => {
  let str = (planned + actual).toFixed(2)
  if (str.includes('.')) {
    while (str.endsWith('0')) {
      str = str.slice(0, -1)
    }
    if (str.endsWith('.')) {
      str = str.slice(0, -1)
    }
  }

  return str
}

const colorIndicator = (col: number): string => {
  if (!props.info.dedicated) return ''

  if (col < props.info.dedicated) {
    return 'orange'
  } else if (col === props.info.dedicated && col !== 0) {
    return 'green'
  } else if (col > props.info.dedicated) {
    return 'red'
  } else {
    return 'grey'
  }
}
</script>

<style scoped lang="scss">
@use '../static/style/variables.scss' as *;
@use '../static/style/mixins.scss' as *;

.counter {
  background: $white;
  display: flex;
  align-items: center;
  column-gap: 16px;

  &:last-child {
    border-right: 1px solid #cccccc;
    padding-right: 32px;
  }

  &.secondary {
    align-items: flex-start;
    justify-content: space-between;
    padding: 20px 17px;
    border: 1px solid #cccccc;
    border-radius: 3px;
    margin-right: 0 !important;

    & + & {
      margin-top: 8px;
    }

    &:last-of-type {
      margin-bottom: 16px;
    }

    .counter__items {
      flex-direction: column;
    }

    .counter__items h4 {
      max-width: unset;
    }

    .counter__proc {
      display: inline-block;
    }
  }

  &:last-child:not(:first-child) {
    border-left: 1px solid #cccccc;
    padding-left: 20px;

    @media (min-width: 1600px) {
      padding-left: 32px;
    }
  }

  &:not(:last-child) {
    margin-right: 32px;

    @media (max-width: 1305px) {
      margin-right: 20px;
    }
  }

  h2 {
    margin-bottom: 0;
    white-space: nowrap;
  }

  h3 {
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    color: #888888;
  }

  &__items {
    display: flex;
    gap: 16px;

    h4 {
      @include font_3;
      color: rgba(117, 117, 117, 1);
    }
  }

  &__item {
    .indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #dddddd;
      display: inline-block;
      margin-right: 4px;

      &.red {
        background: #e52e52;
      }

      &.orange {
        background: #ff6f20;
      }

      &.green {
        background: #30b648;
      }
    }
  }

  &__proc {
    @include subtext;
    color: rgba(117, 117, 117, 1);
  }
}
</style>
